import { useState, useRef, useEffect } from 'react';
import { 
  SparklesIcon, 
  ArrowPathIcon,
  WrenchScrewdriverIcon,
  ArrowDownTrayIcon,
  Bars3Icon,
  TrashIcon,
  Cog6ToothIcon,
  ListBulletIcon,
  TagIcon
} from '@heroicons/react/24/outline';
import smartCategorizationService from '../services/smartCategorizationService';
import toast from 'react-hot-toast';
import axios from 'axios';

/**
 * TransactionActions Component
 * 
 * Provides a dropdown menu with common transaction actions
 */
const TransactionActions = ({ 
  onSyncPlaid,
  onDiagnostics,
  onImport,
  onTransactionUpdate,
  uncategorizedCount = 0,
  selectedCount = 0,
  onDeleteSelected = null,
  onCustomizeColumns = null,
  onManageCategories = null
}) => {
  const [menuOpen, setMenuOpen] = useState(false);
  const [autoCategorizing, setAutoCategorizing] = useState(false);
  const buttonRef = useRef(null);
  const dropdownRef = useRef(null);

  // Handle menu toggle
  const handleMenuToggle = () => {
    setMenuOpen(!menuOpen);
  };

  // Handle menu close
  const handleMenuClose = () => {
    setMenuOpen(false);
  };

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (
        menuOpen && 
        buttonRef.current && 
        !buttonRef.current.contains(event.target) &&
        dropdownRef.current && 
        !dropdownRef.current.contains(event.target)
      ) {
        setMenuOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [menuOpen]);

  // Handle auto-categorization
  const handleBatchAutoCategorize = async () => {
    handleMenuClose();
    try {
      setAutoCategorizing(true);
      toast.loading('Auto-categorizing transactions...', { id: 'auto-categorize' });
      
      console.log('🤖 Starting batch auto-categorization with options:', {
        onlyUncategorized: true,
        minimumConfidence: 0.4,
        includePlaidTransactions: true
      });
      
      // Try first with the updated API path
      let response;
      try {
        response = await smartCategorizationService.batchCategorize({
          onlyUncategorized: true,
          minimumConfidence: 0.4,
          includePlaidTransactions: true
        });
      } catch (initialError) {
        console.warn('⚠️ Initial categorization attempt failed, trying fallback URL:', initialError);
        
        // If the first attempt failed with 404, try direct API call with alternative URL patterns
        if (initialError.response?.status === 404) {
          const token = localStorage.getItem('token');
          const baseUrl = import.meta.env.VITE_API_URL || '';
          const options = {
            onlyUncategorized: true,
            minimumConfidence: 0.4,
            includePlaidTransactions: true
          };
          
          // Try alternative URL patterns
          const alternativeUrls = [
            `/api/smart-categorization/batch-categorize`,
            `/smart-categorization/batch-categorize`,
            `/api/categorize-transactions`
          ];
          
          let fallbackResponse = null;
          
          for (const url of alternativeUrls) {
            try {
              console.log(`🔄 Trying alternative URL: ${baseUrl}${url}`);
              fallbackResponse = await axios.post(`${baseUrl}${url}`, options, {
                headers: { 
                  Authorization: `Bearer ${token}`,
                  'Content-Type': 'application/json'
                }
              });
              if (fallbackResponse.data) {
                console.log('✅ Alternative URL worked:', url);
                break;
              }
            } catch (fallbackError) {
              console.log(`❌ Alternative URL failed: ${url}`, fallbackError.message);
            }
          }
          
          if (fallbackResponse?.data) {
            response = { success: true, data: fallbackResponse.data };
          } else {
            throw initialError; // Re-throw if all alternatives failed
          }
        } else {
          throw initialError; // Re-throw for non-404 errors
        }
      }
      
      console.log('🤖 Auto-categorization response:', response);
      
      if (response.success) {
        const { updated, skipped, processed } = response.data;
        
        toast.success(
          `Auto-categorized ${updated} of ${processed} transactions`,
          { id: 'auto-categorize' }
        );
        
        if (onTransactionUpdate) {
          console.log('🔄 Triggering transaction update after auto-categorization');
          setTimeout(() => {
            onTransactionUpdate();
          }, 500);
        }
      } else {
        console.error('❌ Failed to auto-categorize:', response);
        toast.error(`Failed to auto-categorize: ${response.message || 'Unknown error'}`, { id: 'auto-categorize' });
      }
    } catch (error) {
      console.error('❌ Error auto-categorizing transactions:', error);
      const errorMessage = error.response?.data?.message || error.message || 'An error occurred during auto-categorization';
      toast.error(errorMessage, { id: 'auto-categorize' });
    } finally {
      setAutoCategorizing(false);
    }
  };

  // Handle delete selected
  const handleDeleteSelected = () => {
    handleMenuClose();
    if (onDeleteSelected) {
      onDeleteSelected();
    }
  };

  return (
    <div className="relative">
      <button
        ref={buttonRef}
        onClick={handleMenuToggle}
        className="h-8 w-8 flex items-center justify-center rounded-lg text-gray-600 hover:bg-gray-100 bg-white/80 backdrop-blur-sm"
        aria-label="Table Actions & Settings"
        title="Table Actions & Settings"
      >
        <Cog6ToothIcon className="h-5 w-5" />
      </button>

      {/* Dropdown Menu */}
      {menuOpen && (
        <div 
          ref={dropdownRef}
          className="absolute right-0 mt-1 z-50 w-48 shadow-xl"
          style={{ filter: 'drop-shadow(0 4px 6px rgba(0, 0, 0, 0.1))' }}
        >
          <div role="menu" aria-orientation="vertical" className="overflow-hidden rounded-lg">
            {/* Auto-categorize Option */}
            <button
              onClick={handleBatchAutoCategorize}
              disabled={autoCategorizing}
              className="flex items-center w-full h-11 px-4 text-sm text-gray-700 bg-white/90 backdrop-blur-sm hover:bg-gray-100 border-b border-gray-100"
              role="menuitem"
            >
              <SparklesIcon className="mr-3 h-5 w-5 flex-shrink-0 text-indigo-500" />
              <div className="truncate">
                <span>Auto-categorize</span>
                {uncategorizedCount > 0 && <span className="ml-1">({uncategorizedCount})</span>}
                {autoCategorizing && <span className="ml-1">...</span>}
              </div>
            </button>

            {/* Sync Plaid Option */}
            <button
              onClick={() => {
                handleMenuClose();
                onSyncPlaid && onSyncPlaid();
              }}
              className="flex items-center w-full h-11 px-4 text-sm text-gray-700 bg-white/90 backdrop-blur-sm hover:bg-gray-100 border-b border-gray-100"
              role="menuitem"
            >
              <ArrowPathIcon className="mr-3 h-5 w-5 flex-shrink-0 text-blue-500" />
              <span>Sync Plaid</span>
            </button>

            {/* Delete Selected Option */}
            <button
              onClick={handleDeleteSelected}
              disabled={selectedCount === 0 || !onDeleteSelected}
              className={`flex items-center w-full h-11 px-4 text-sm ${
                selectedCount > 0 ? 'text-red-600 hover:bg-red-50' : 'text-gray-400 cursor-not-allowed'
              } bg-white/90 backdrop-blur-sm border-b border-gray-100`}
              role="menuitem"
            >
              <TrashIcon className={`mr-3 h-5 w-5 flex-shrink-0 ${
                selectedCount > 0 ? 'text-red-500' : 'text-gray-400'
              }`} />
              <div className="truncate">
                <span>Delete Selected</span>
                {selectedCount > 0 && <span className="ml-1">({selectedCount})</span>}
              </div>
            </button>

            {/* Diagnostics Option */}
            <button
              onClick={() => {
                handleMenuClose();
                onDiagnostics && onDiagnostics();
              }}
              className="flex items-center w-full h-11 px-4 text-sm text-gray-700 bg-white/90 backdrop-blur-sm hover:bg-gray-100 border-b border-gray-100"
              role="menuitem"
            >
              <WrenchScrewdriverIcon className="mr-3 h-5 w-5 flex-shrink-0 text-yellow-500" />
              <span>Diagnostics</span>
            </button>

            {/* Import Option */}
            <button
              onClick={() => {
                handleMenuClose();
                onImport && onImport();
              }}
              className="flex items-center w-full h-11 px-4 text-sm text-gray-700 bg-white/90 backdrop-blur-sm hover:bg-gray-100 border-b border-gray-100"
              role="menuitem"
            >
              <ArrowDownTrayIcon className="mr-3 h-5 w-5 flex-shrink-0 text-green-500" />
              <span>Import</span>
            </button>

            {/* Customize Columns Option */}
            {onCustomizeColumns && (
              <button
                onClick={() => {
                  handleMenuClose();
                  onCustomizeColumns();
                }}
                className="flex items-center w-full h-11 px-4 text-sm text-gray-700 bg-white/90 backdrop-blur-sm hover:bg-gray-100 border-b border-gray-100"
                role="menuitem"
              >
                <div className="mr-3 flex items-center gap-1">
                  <Cog6ToothIcon className="h-4 w-4 flex-shrink-0 text-indigo-500" />
                  <ListBulletIcon className="h-4 w-4 flex-shrink-0 text-indigo-500" />
                </div>
                <span>Customize Columns</span>
              </button>
            )}

            {/* Manage Categories Option */}
            {onManageCategories && (
              <button
                onClick={() => {
                  handleMenuClose();
                  onManageCategories();
                }}
                className="flex items-center w-full h-11 px-4 text-sm text-gray-700 bg-white/90 backdrop-blur-sm hover:bg-gray-100"
                role="menuitem"
              >
                <TagIcon className="mr-3 h-5 w-5 flex-shrink-0 text-purple-500" />
                <span>Categories</span>
              </button>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default TransactionActions; 